import { DataClient } from "@/lib/data-client";
import { authenticatedFetch, invokeFunction, requestJson, requestStream } from "@/lib/api-client";
import type { PublicSession } from "@/lib/session";

let csrfToken: string | null = null;
let currentSession: PublicSession | null = null;
const events = new EventTarget();
const uploadedFileIds = new Map<string, string>();

export function configureAwsSession(token: string | null, session: PublicSession | null = null) {
  csrfToken = token;
  currentSession = session;
  events.dispatchEvent(new Event("change"));
}
export const getCsrfToken = () => csrfToken;

const data = new DataClient(() => csrfToken);
type Callback = (payload: { new: Record<string, unknown>; old: Record<string, unknown> }) => void;
type RealtimeFilter = { event?: string; schema?: string; table?: string; filter?: string };
type Subscription = { event: string; filter: RealtimeFilter; callback: Callback; previousRows?: Map<string, Record<string, unknown>> };

function dataFilters(filter: string | undefined): Array<{ column: string; operator: string; value: string }> {
  if (!filter) return [];
  const match = /^([A-Za-z_][A-Za-z0-9_]*)=(eq|neq|gt|gte|lt|lte|ilike)\.(.+)$/.exec(filter);
  if (!match) throw new Error("Unsupported polling subscription filter.");
  return [{ column: match[1]!, operator: match[2]!, value: decodeURIComponent(match[3]!) }];
}

export class PollingChannel {
  private subscriptions: Subscription[] = [];
  private timer: number | null = null;
  private stream: AbortController | null = null;
  private active = false;
  private polling = false;
  constructor(private name: string) {}
  on(event: string, filter: RealtimeFilter, callback: Callback) {
    this.subscriptions.push({ event, filter, callback });
    return this;
  }
  private notifyAll(payload: { new: Record<string, unknown>; old: Record<string, unknown> }) {
    for (const subscription of this.subscriptions) subscription.callback(payload);
  }
  private async poll() {
    if (!this.active || this.polling) return;
    this.polling = true;
    try {
    for (const subscription of this.subscriptions) {
      const table = subscription.filter.table;
      if (!table) continue;
      try {
        const result = await requestJson<{ data?: unknown }>("/api/data", {
          method: "POST",
          csrfToken,
          body: {
            version: 1,
            table,
            action: "select",
            filters: dataFilters(subscription.filter.filter),
            limit: 500,
            expect: "many",
          },
        });
        if (!this.active) return;
        const rows = (Array.isArray(result.data) ? result.data : []).filter(row => row && typeof row === 'object') as Record<string, unknown>[];
        const current = new Map(rows.map(row => [typeof row.id === 'string' ? row.id : JSON.stringify(row), row]));
        const previous = subscription.previousRows;
        subscription.previousRows = current;
        if (!previous) continue;
        const emit = (event: string, newer: Record<string, unknown>, older: Record<string, unknown>) => {
          if (!subscription.filter.event || subscription.filter.event === '*' || subscription.filter.event === event) subscription.callback({ new: newer, old: older });
        };
        for (const [key, row] of current) {
          const old = previous.get(key);
          if (!old) emit('INSERT', row, {});
          else if (JSON.stringify(old) !== JSON.stringify(row)) emit('UPDATE', row, old);
        }
        for (const [key, old] of previous) if (!current.has(key)) emit('DELETE', {}, old);
      } catch (error) {
        console.error("Subscription poll failed", error);
      }
    }
    } finally { this.polling = false; }
  }
  subscribe() {
    if (this.active) return this;
    this.active = true;
    const runId = this.name.startsWith("chat-job-") ? this.name.slice(9) : null;
    if (runId) {
      const controller = new AbortController();
      this.stream = controller;
      void this.watchJob(runId, controller.signal);
      return this;
    }
    void this.poll();
    this.timer = globalThis.setInterval(() => { void this.poll(); }, 3000) as unknown as number;
    return this;
  }
  unsubscribe() {
    this.active = false;
    if (this.timer !== null) globalThis.clearInterval(this.timer);
    this.timer = null;
    this.stream?.abort();
  }
  private async watchJob(runId: string, signal: AbortSignal) {
    let cursor = 0;
    while (!signal.aborted) {
      try {
        const response = await requestStream(`/api/functions/job-events?jobId=${encodeURIComponent(runId)}`, signal, { 'Last-Event-ID': String(cursor) });
        if (!response.headers.get('content-type')?.includes('text/event-stream')) throw new Error('Job events did not return an event stream.');
        for (const frame of (await response.text()).replace(/\r\n/g, '\n').split('\n\n')) {
          const lines = frame.split('\n');
          const sequence = Number(lines.find(line => line.startsWith('id:'))?.slice(3).trim());
          if (!Number.isSafeInteger(sequence) || sequence <= cursor) continue;
          const payload = JSON.parse(lines.filter(line => line.startsWith('data:')).map(line => line.slice(5).trim()).join('\n'));
          cursor = sequence;
          if (signal.aborted) return;
          this.notifyAll({ new: payload, old: {} });
          if (['succeeded', 'failed', 'partial', 'cancelled'].includes(payload.status)) return;
        }
      } catch (error) {
        if (signal.aborted) return;
        console.error('Job subscription failed', error);
        return;
      }
      await new Promise<void>(resolve => {
        const done = () => { globalThis.clearTimeout(timer); signal.removeEventListener('abort', done); resolve(); };
        const timer = globalThis.setTimeout(done, 1500);
        signal.addEventListener('abort', done, { once: true });
      });
    }
  }
}

type FileRoute = "chat-upload" | "project-attachment" | "design-asset" | "design-system-asset" | "app-dataset" | "sandbox-execution" | "hosted-agent-inline" | "skill-import" | "skill-file" | "knowledge-import" | "persona-import";
type UploadOptions = { contentType?: string; upsert?: boolean; route?: FileRoute; scopeId?: string };
type SinglePreflight = { kind: "single"; fileId: string; uploadUrl: string };
type MultipartPreflight = { kind: "multipart"; fileId: string; partSize: number; partUrls: string[] };
type Preflight = SinglePreflight | MultipartPreflight;
const MULTIPART_PART_BYTES = 10 * 1024 * 1024;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
type PendingVerification = { state: "pending"; verificationId: string };
type VerifiedFile = { state: "verified"; id: string; storagePath: string };
type VerificationResponse = { state?: string; verificationId?: string; id?: string; storagePath?: string; lastError?: string };
type PendingUploadRecord = { verificationId: string; path: string; filename: string; size: number; createdAt: string };
const PENDING_UPLOADS_KEY = "askbell.pending-file-verifications.v1";
function pendingUploads(): Record<string, PendingUploadRecord> { try { return JSON.parse(globalThis.localStorage?.getItem(PENDING_UPLOADS_KEY) ?? "{}") as Record<string, PendingUploadRecord>; } catch { return {}; } }
function savePending(record: PendingUploadRecord) { const records = pendingUploads(); records[record.path] = record; globalThis.localStorage?.setItem(PENDING_UPLOADS_KEY, JSON.stringify(records)); }
function clearPending(path: string) { const records = pendingUploads(); delete records[path]; globalThis.localStorage?.setItem(PENDING_UPLOADS_KEY, JSON.stringify(records)); }
export function pendingFileVerifications(): PendingUploadRecord[] { return Object.values(pendingUploads()); }

async function awaitVerified(completed: unknown, pending?: Omit<PendingUploadRecord, "verificationId">): Promise<VerifiedFile> {
  const initial = completed as VerificationResponse;
  if (initial.state === "verified" && typeof initial.id === "string" && typeof initial.storagePath === "string" && initial.storagePath.length > 0) return initial as VerifiedFile;
  if (initial.state !== "pending" || typeof initial.verificationId !== "string") {
    throw new Error("The upload did not return a verification identifier.");
  }

  if (pending) savePending({ ...pending, verificationId: initial.verificationId });
  // The object is intentionally unavailable while the worker hashes the stored
  // bytes. This bounded exponential backoff keeps the visible upload pending
  // and never substitutes the verification ID for an object-link ID.
  for (let attempt = 0; attempt < 9; attempt += 1) {
    const delayMs = Math.min(250 * 2 ** attempt, 2_000);
    await new Promise<void>((resolve) => globalThis.setTimeout(resolve, delayMs));
    const result = await requestJson<VerificationResponse>("/api/files/verification", {
      method: "POST",
      csrfToken,
      body: { verificationId: initial.verificationId },
    });
    if (result.state === "verified" && typeof result.id === "string" && typeof result.storagePath === "string" && result.storagePath.length > 0) { if (pending) clearPending(pending.path); return result as VerifiedFile; }
    if (result.state === "verified") throw new Error("The verified upload metadata is incomplete.");
    if (result.state === "failed") { if (pending) clearPending(pending.path); throw new Error(result.lastError || "Stored file verification failed."); }
  }
  throw new Error("The file is still pending verification. Try again after verification completes.");
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));
  }
  return btoa(binary);
}

async function sha256(blob: Blob): Promise<string> {
  return bytesToBase64(new Uint8Array(await crypto.subtle.digest("SHA-256", await blob.arrayBuffer())));
}

function uploadTarget(purpose: string, path: string, options?: UploadOptions): { route: FileRoute; scopeId: string } {
  if (options?.route && options.scopeId && UUID.test(options.scopeId)) return { route: options.route, scopeId: options.scopeId };
  const segments = path.split("/").filter(Boolean);
  if (purpose === "app-datasets" && UUID.test(segments[1] ?? "")) return { route: "app-dataset", scopeId: segments[1]! };
  if (purpose === "chat-uploads" && segments[0] === "design" && UUID.test(segments[1] ?? "")) return { route: "design-asset", scopeId: segments[1]! };
  if (purpose === "chat-uploads" && UUID.test(segments[1] ?? "")) return { route: "chat-upload", scopeId: segments[1]! };
  throw new Error(`Storage purpose "${purpose}" does not identify a supported AWS file route and UUID scope.`);
}

async function put(url: string, body: Blob, checksumSha256: string, contentType?: string): Promise<Response> {
  const headers = new Headers({ "x-amz-checksum-sha256": checksumSha256 });
  if (contentType) headers.set("Content-Type", contentType);
  const response = await fetch(url, { method: "PUT", headers, body });
  if (!response.ok) {
    const xml = await response.text();
    const code = xml.match(/<Code>([A-Za-z0-9]+)<\/Code>/)?.[1];
    if (code === "AccessDenied") console.error("S3 upload access denied", xml.match(/<Message>([^<]{1,1000})<\/Message>/)?.[1] ?? "No reason supplied");
    throw new Error(`File upload failed (${response.status}${code ? `: ${code}` : ""}).`);
  }
  return response;
}

export async function resumePendingUpload(path: string): Promise<VerifiedFile> {
  const pending = pendingUploads()[path];
  if (!pending) throw new Error("No pending verification exists for this file.");
  const verified = await awaitVerified({ state: "pending", verificationId: pending.verificationId }, pending);
  uploadedFileIds.set(path, verified.id);
  return verified;
}

async function upload(purpose: string, path: string, file: File, options?: UploadOptions) {
  const existing = pendingUploads()[path];
  if (existing) { const verified = await resumePendingUpload(path); return { data: verified, error: null }; }
  let fileId: string | null = null;
  let isMultipart = false;
  try {
    const target = uploadTarget(purpose, path, options);
    const contentType = options?.contentType || file.type || "application/octet-stream";
    const checksumSha256 = await sha256(file);
    const partChecksums: string[] = [];
    if (file.size > 16 * 1024 * 1024) {
      for (let offset = 0; offset < file.size; offset += MULTIPART_PART_BYTES) {
        partChecksums.push(await sha256(file.slice(offset, Math.min(offset + MULTIPART_PART_BYTES, file.size))));
      }
    }
    const preflight = await requestJson<Preflight>("/api/files/preflight", {
      method: "POST",
      csrfToken,
      body: {
        route: target.route,
        scopeId: target.scopeId,
        filename: file.name,
        contentType,
        byteLength: file.size,
        checksumSha256,
        ...(partChecksums.length ? { partChecksums } : {}),
      },
    });
    fileId = preflight.fileId;
    isMultipart = preflight.kind === "multipart";
    if (preflight.kind === "single") {
      await put(preflight.uploadUrl, file, checksumSha256, contentType);
      const completed = await awaitVerified(await requestJson<any>("/api/files/complete", { method: "POST", csrfToken, body: { fileId } }), { path, filename: file.name, size: file.size, createdAt: new Date().toISOString() });
      uploadedFileIds.set(path, completed.id);
      return { data: completed, error: null };
    }
    if (preflight.partSize !== MULTIPART_PART_BYTES || preflight.partUrls.length !== partChecksums.length) {
      throw new Error("The multipart upload plan does not match the file checksum plan.");
    }
    const parts: Array<{ partNumber: number; etag: string; checksumSha256: string }> = [];
    for (let index = 0; index < preflight.partUrls.length; index += 1) {
      const start = index * preflight.partSize;
      const part = file.slice(start, Math.min(start + preflight.partSize, file.size));
      const response = await put(preflight.partUrls[index]!, part, partChecksums[index]!);
      const etag = response.headers.get("etag");
      if (!etag) throw new Error("The multipart upload response did not expose an ETag header.");
      parts.push({ partNumber: index + 1, etag, checksumSha256: partChecksums[index]! });
    }
    const completed = await awaitVerified(await requestJson<any>("/api/files/complete", { method: "POST", csrfToken, body: { fileId, parts } }), { path, filename: file.name, size: file.size, createdAt: new Date().toISOString() });
    uploadedFileIds.set(path, completed.id);
    return { data: completed, error: null };
  } catch (error) {
    if (fileId && isMultipart) {
      await requestJson("/api/files/abort", { method: "POST", csrfToken, body: { fileId } }).catch(() => undefined);
    }
    return { data: null, error: error as Error };
  }
}

const fileIdFor = async (value: string): Promise<string> => {
  const local = uploadedFileIds.get(value);
  if (local) return local;
  if (UUID.test(value)) return value;
  const result = await requestJson<{fileId:string}>('/api/files/resolve', {method:'POST', body:{path:value}});
  return result.fileId;
};
async function remove(_purpose: string, paths: string[]) {
  try {
    for (const path of paths) {
      const fileId = await fileIdFor(path);
      if (!fileId) throw new Error("The file does not have an AWS object-link identifier.");
      await requestJson("/api/files/delete", { method: "POST", csrfToken, body: { fileId } });
      uploadedFileIds.delete(path);
      clearPending(path);
    }
    return { data: { ok: true }, error: null };
  } catch (error) { return { data: null, error: error as Error }; }
}
async function signed(value: string) {
  try {
    const fileId = await fileIdFor(value);
    if (!fileId) throw new Error("The file does not have an AWS object-link identifier.");
    const result = await requestJson<{ url: string }>("/api/files/download", { method: "POST", csrfToken, body: { fileId } });
    return { data: { signedUrl: result.url }, error: null };
  } catch (error) { return { data: null, error: error as Error }; }
}
async function download(value: string) {
  try {
    const fileId = await fileIdFor(value);
    if (!fileId) throw new Error("The file does not have an AWS object-link identifier.");
    const result = await requestJson<{ url: string }>("/api/files/download", { method: "POST", csrfToken, body: { fileId } });
    const response = await fetch(result.url);
    if (!response.ok) throw new Error(`File download failed (${response.status}).`);
    return { data: await response.blob(), error: null };
  } catch (error) { return { data: null, error: error as Error }; }
}

export async function invokeMultipart<T = any>(name: string, body: FormData): Promise<T> {
  const headers = new Headers({ Accept: "application/json" });
  if (csrfToken) headers.set("X-CSRF-Token", csrfToken);
  const response = await authenticatedFetch(`/api/functions/${encodeURIComponent(name)}`, { method: "POST", headers, body });
  if (!response.ok) throw new Error((await response.text().catch(() => "")) || `The request failed (${response.status}).`);
  return response.json() as Promise<T>;
}

export const aws = {
  from: data.from.bind(data),
  rpc: <T = any>(name: string, params?: Record<string, unknown>) => invokeFunction<any, T>(name, params, csrfToken).then((value) => ({ data: value, error: null })).catch((error: Error) => ({ data: null, error })),
  functions: { invoke: <T = any>(name: string, options: { body?: any } = {}) => invokeFunction<any, T>(name, options.body, csrfToken).then((value) => ({ data: value, error: null })).catch((error: Error) => ({ data: null, error })) },
  channel: (name: string) => new PollingChannel(name),
  removeChannel: (channel: PollingChannel) => channel.unsubscribe(),
  storage: { from: (purpose: string) => ({ upload: (path: string, file: File, options?: UploadOptions) => upload(purpose, path, file, options), remove: (paths: string[]) => remove(purpose, paths), download, createSignedUrl: (fileId: string, _ttl?: number) => signed(fileId) }) },
  auth: {
    getSession: async () => ({ data: { session: currentSession ? { ...currentSession, csrfToken: currentSession.csrfToken } : null }, error: null }),
    getUser: async () => ({ data: { user: currentSession?.user ?? null }, error: null }),
    signOut: () => requestJson("/api/auth/logout", { method: "POST", csrfToken }),
    onAuthStateChange: (listener: () => void) => { events.addEventListener("change", listener); return { data: { subscription: { unsubscribe: () => events.removeEventListener("change", listener) } } }; },
  },
};



